import { Monitor, Moon, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ThemeChoice } from '../../theme/theme';
import { useTheme } from '../../theme/themeStore';
import { tapTarget } from '../../components/styles';

const OPTIONS: { choice: ThemeChoice; label: string; icon: LucideIcon }[] = [
  { choice: 'system', label: 'System', icon: Monitor },
  { choice: 'light', label: 'Light', icon: Sun },
  { choice: 'dark', label: 'Dark', icon: Moon },
];

const option = `inline-flex items-center gap-2 ${tapTarget} px-4 py-2 rounded-xl text-sm font-medium border cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-primary/30`;
const chosen = `${option} border-primary bg-primary-container text-on-primary-container`;
const notChosen = `${option} border-outline/30 hover:bg-surface-variant`;

/** Light or dark on this browser. Self-contained: a device preference, not part of the profile. */
export default function ThemeSetting() {
  const { choice, theme, problem, saveError, choose } = useTheme();
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm text-on-surface-variant">
        Light or dark, on this device. System follows the device's dark mode, and changes when it does.
      </legend>
      <div className="flex flex-wrap gap-2 pt-3">
        {OPTIONS.map(({ choice: value, label, icon: Icon }) => (
          <label key={value} className={value === choice ? chosen : notChosen}>
            <input type="radio" name="theme" value={value} checked={value === choice} onChange={() => choose(value)} className="sr-only" />
            <Icon className="w-4 h-4" aria-hidden="true" />
            {label}
          </label>
        ))}
      </div>
      {choice === 'system' && <p className="text-sm text-on-surface-variant">This device is in {theme} mode now.</p>}
      {problem === 'unknown' && (
        <p className="text-sm text-tertiary">The theme saved on this device is not one this site knows, so it follows System. Choose one to replace it.</p>
      )}
      {problem === 'unreadable' && (
        <p className="text-sm text-tertiary">This browser does not let the site read its saved settings, so the theme follows System.</p>
      )}
      {saveError !== null && <p className="text-sm text-error">Not saved: {saveError}</p>}
    </fieldset>
  );
}
